import React from "react";
import PropTypes from "prop-types";
import { generateSmallThumbnailUrl } from "../../utils/ImageUtils";
import { getFullScreenChangeEvent } from "../../utils/Fullscreen";
import GTMTracker from "../../utils/tracking/GTMTracker";
import scroll from "scroll";
import { getPaddedDurationFromMilliSeconds } from "../../utils/TimeFormatter";
import { FALLBACK_IMAGE } from "../../constants/Urls";

const CURRENT_ITEM_CLASS = "personalized-list__link--current";
const MAX_PLAYLIST_VIDEOS_LIMIT = 90;

export class PersonalizedList extends React.PureComponent {
    constructor(...args) {
        super(...args);
        this._playlistScroll = this._playlistScroll.bind(this);
        this._scrollSelectedVideoToTop =
            this._scrollSelectedVideoToTop.bind(this);
        // To keep track of playlist fetch status for corresponding length
        this._playlistFetchStatus = {};
    }

    componentDidMount() {
        document.addEventListener(
            getFullScreenChangeEvent(),
            this._scrollSelectedVideoToTop
        );
        this.props.onLoadUserRecommendation();
        this._scrollSelectedVideoToTop();
    }

    componentWillUnmount() {
        document.removeEventListener(
            getFullScreenChangeEvent(),
            this._scrollSelectedVideoToTop
        );
    }

    componentDidUpdate({ currentVideoBmmrId: prevVideoBmmrId }) {
        const { currentVideoBmmrId } = this.props;

        if (prevVideoBmmrId !== currentVideoBmmrId) {
            this._scrollSelectedVideoToTop();
        }
    }

    render() {
        const videoStories = this.props.videoList;
        const { currentVideoBmmrId, shouldShowVideoList, isLandingPageVideo } =
            this.props;
        const videoEls = videoStories.map((videoStory, index) => {
            const isNowPlaying =
                !isLandingPageVideo &&
                currentVideoBmmrId === videoStory.video.bmmrId;
            const additionalClass = isNowPlaying ? CURRENT_ITEM_CLASS : "";
            const durationHtml = getDurationHtml(
                isNowPlaying,
                videoStory.video.duration
            );

            // default vertical thumbnail to a placeholder due to page designed for 16:9
            const thumbnailHtml = getThumbnailHtml(
                videoStory.video,
                videoStory.isVertical
            );
            const nowPlayingHtml = this._getNowPlayingHtml(isNowPlaying);

            return (
                <li key={videoStory.video.bmmrId}>
                    <div className="personalized-list__item">
                        <a
                            href={videoStory.url}
                            className={`personalized-list__link ${additionalClass}`}
                            onClick={this._onSelectVideo.bind(
                                this,
                                videoStory.video.bmmrId,
                                index
                            )}
                        >
                            <div className="personalized-list__details">
                                {nowPlayingHtml}
                                {durationHtml}
                                <h3
                                    dangerouslySetInnerHTML={{
                                        __html: videoStory.headline.html,
                                    }}
                                />
                            </div>
                            {thumbnailHtml}
                        </a>
                    </div>
                </li>
            );
        });

        const additionalContainerClass = shouldShowVideoList
            ? ""
            : "personalized-list--hidden";
        return (
            <div
                className={`personalized-list ${additionalContainerClass}`}
                onScroll={this._playlistScroll}
                ref={(element) => (this._containerEl = element)}
            >
                <ul className="personalized-list__container" tabIndex="0">
                    {videoEls}
                </ul>
            </div>
        );
    }

    _scrollSelectedVideoToTop() {
        const viewportEl = this._containerEl;
        const selectedEl =
            viewportEl.getElementsByClassName(CURRENT_ITEM_CLASS)[0];
        if (selectedEl) {
            const newScrollTop =
                selectedEl.getBoundingClientRect().top -
                viewportEl.getBoundingClientRect().top +
                viewportEl.scrollTop;
            scroll.top(viewportEl, newScrollTop);
        }
    }

    _onSelectVideo(bmmrId, videoIndex, event) {
        const { onSelectVideo, eventPrefix } = this.props;

        GTMTracker.trackEngagement({
            bvpEventAction: "click",
            bvpEventLabel: `${eventPrefix}.for_you_video_${videoIndex}`,
        });

        onSelectVideo(bmmrId, event);

        if (
            window.matchMedia &&
            window.matchMedia("(max-width: 768px)").matches
        ) {
            scroll.top(document.body, 0);
        }
    }

    _playlistScroll() {
        if (
            this._isLastElementInViewport() &&
            !(this._isPlaylistLimitReached() || this._isPlaylistFetching())
        ) {
            this._trackScrollToBottom();
            this.props.onPlaylistEndReached();
            this._updatePlaylistFetchingStatus();
        }
    }

    _isPlaylistLimitReached() {
        return this.props.videoList.length >= MAX_PLAYLIST_VIDEOS_LIMIT;
    }

    _isPlaylistFetching() {
        return this._playlistFetchStatus[this.props.videoList.length];
    }

    _isLastElementInViewport() {
        const viewportEl = this._containerEl;
        const viewportHeight = viewportEl.getBoundingClientRect().height;

        if (viewportHeight === 0) {
            return false;
        }

        const offset = viewportEl.scrollHeight - viewportEl.scrollTop;
        return offset === Math.round(viewportHeight);
    }

    _updatePlaylistFetchingStatus() {
        this._playlistFetchStatus[this.props.videoList.length] = true;
    }

    _trackScrollToBottom() {
        const playListBottomReachCount =
            Object.keys(this._playlistFetchStatus).length + 1;
        GTMTracker.trackScrollTracking({
            bvpEventAction: "scroll",
            bvpEventLabel: `personalized-list-bottom-${playListBottomReachCount}`,
        });
    }

    _getNowPlayingHtml(isNowPlaying) {
        if (!isNowPlaying) {
            return "";
        }

        return (
            <div className="personalized-list__now-playing">
                {this.props.localizer("NOW_PLAYING")}
            </div>
        );
    }
}

function getThumbnailHtml(video, isVertical) {
    if (!video.thumbnail) {
        return "";
    }

    return (
        <img
            className="personalized-list__thumbnail"
            loading="lazy"
            src={generateSmallThumbnailUrl(
                isVertical ? FALLBACK_IMAGE : video.thumbnail.url
            )}
            alt=""
        />
    );
}

function getDurationHtml(isNowPlaying, duration) {
    if (isNowPlaying) {
        return "";
    }

    return (
        <div className="personalized-list__duration">
            {getPaddedDurationFromMilliSeconds(duration)}
        </div>
    );
}

PersonalizedList.defaultProps = {
    shouldShowVideoList: true,
    onSelectVideo: () => {},
    onLoadUserRecommendation: () => {},
    onPlaylistEndReached: () => {},
    eventPrefix: "",
    localizer: (input) => input,
};

PersonalizedList.propTypes = {
    videoList: PropTypes.array.isRequired,
    shouldShowVideoList: PropTypes.bool,
    currentVideoBmmrId: PropTypes.string,
    onSelectVideo: PropTypes.func,
    onLoadUserRecommendation: PropTypes.func,
    onPlaylistEndReached: PropTypes.func,
    eventPrefix: PropTypes.string,
    localizer: PropTypes.func,
    isLandingPageVideo: PropTypes.bool,
};
